import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import type { FileCustom } from "../interfaces";
import { getListOfLocationsByProject } from "../../../../shared/util/requestDataServer";
import { projectList } from "../../../../shared/util/projectList";
import { formatDateToYYYYMMDD } from "../../../../shared/util/formatDates";
import type { SimpleLocation } from "../tam/types";
import { dedupeLocations, tamLocationFilterOptions } from "../tam/lib/tamLocationSearch";
import {
  matchLabel,
  matchTamWellToLocation,
  matchTamWellWithHints,
  type ActivityWellHint,
  type TamLocationMatchKind,
} from "../tam/lib/tamLocationMatch";
import { inferTamStudyDate, parseTamFile, tamWellNames } from "../tam/lib/tamUploadUtils";
import "./tamUpload.scss";

export interface TamConsolidateItem {
  id: string;
  file: File;
  parsing: boolean;
  parseError: string | null;
  executedAt: string;
  wells: {
    tamName: string;
    matchKind: TamLocationMatchKind;
    location: SimpleLocation | null;
    fromActivity?: boolean;
  }[];
}

export interface TamConsolidateReadyPayload {
  file: File;
  executedAt: string;
  locationIds: string[];
  wellNames: string[];
}

interface TamConsolidateTabProps {
  tamFiles: FileCustom[];
  selectedFieldId: string;
  executedDate: Date | string | null;
  /** Mantiene en sync la Fecha de Generalidades cuando se edita desde el tab TAM. */
  onExecutedDateChange?: (date: Date | null) => void;
  activityWellHints: ActivityWellHint[];
  onReadyChange: (ready: TamConsolidateReadyPayload[]) => void;
}

function todayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function newItemId(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

function tamFilesKey(files: FileCustom[]): string {
  return files.map((f) => newItemId(f.file)).sort().join("|");
}

function defaultStudyDate(executedDate: Date | string | null, parsedFallback?: string | null): string {
  if (executedDate) {
    try {
      const iso = formatDateToYYYYMMDD(executedDate);
      // Si viene string "yyyy-MM-dd" lo usamos; si trae hora u otro formato, recortamos.
      if (/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso.slice(0, 10);
      return iso;
    } catch {
      /* use fallbacks */
    }
  }
  return parsedFallback ?? todayIso();
}

/** Convierte yyyy-MM-dd a Date local (sin shift UTC). */
function isoToLocalDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function MatchIcon({ kind }: { kind: TamLocationMatchKind }) {
  if (kind === "exact") return <CheckCircleOutlineIcon fontSize="small" color="success" />;
  if (kind === "fuzzy") return <HelpOutlineIcon fontSize="small" color="warning" />;
  return <ErrorOutlineIcon fontSize="small" color="error" />;
}

export function TamConsolidateTab({
  tamFiles,
  selectedFieldId,
  executedDate,
  onExecutedDateChange,
  activityWellHints,
  onReadyChange,
}: TamConsolidateTabProps) {
  const [locations, setLocations] = useState<SimpleLocation[]>([]);
  const [loadingLocs, setLoadingLocs] = useState(false);
  const [items, setItems] = useState<TamConsolidateItem[]>([]);
  const parsedKeyRef = useRef<string>("");
  const executedDateRef = useRef(executedDate);
  executedDateRef.current = executedDate;
  /** Evita loop al empujar cambios TAM → Generalidades → TAM. */
  const syncingFromTamRef = useRef(false);

  const resolvedProjectId = useMemo(() => {
    const field = projectList.find(
      (p) => p.id === selectedFieldId || p.projectId === selectedFieldId,
    );
    return field?.projectId ?? selectedFieldId;
  }, [selectedFieldId]);

  const rematchWells = useCallback(
    (names: string[]): TamConsolidateItem["wells"] =>
      names.map((tamName) => {
        const match = matchTamWellWithHints(tamName, locations, activityWellHints);
        const fromActivity = activityWellHints.some(
          (h) =>
            match.location &&
            h.locationId === match.location!.id &&
            (match.kind === "exact" || match.kind === "fuzzy"),
        );
        return { tamName, matchKind: match.kind, location: match.location, fromActivity };
      }),
    [locations, activityWellHints],
  );

  useEffect(() => {
    if (!resolvedProjectId) {
      setLocations([]);
      return;
    }
    setLoadingLocs(true);
    getListOfLocationsByProject(resolvedProjectId)
      .then((rows) => setLocations(dedupeLocations((rows as SimpleLocation[]) || [])))
      .finally(() => setLoadingLocs(false));
  }, [resolvedProjectId]);

  useEffect(() => {
    if (!tamFiles.length) {
      setItems([]);
      parsedKeyRef.current = "";
      return;
    }

    const fileKey = tamFilesKey(tamFiles);
    if (fileKey === parsedKeyRef.current && items.some((i) => !i.parsing && i.wells.length > 0)) {
      return;
    }

    let cancelled = false;
    parsedKeyRef.current = fileKey;

    const parseAll = async () => {
      setItems(
        tamFiles.map((f) => ({
          id: newItemId(f.file),
          file: f.file,
          parsing: true,
          parseError: null,
          executedAt: defaultStudyDate(executedDateRef.current),
          wells: [],
        })),
      );

      await Promise.all(
        tamFiles.map(async (f) => {
          const id = newItemId(f.file);
          try {
            const parsed = await parseTamFile(f.file);
            const names = tamWellNames(parsed);
            if (!names.length) throw new Error("No se encontraron pozos en el archivo");
            const inferred = inferTamStudyDate(parsed);
            // Preferir siempre la fecha actual de Generalidades (puede haber cambiado durante el parse).
            const executedAt = defaultStudyDate(executedDateRef.current, inferred);
            if (cancelled) return;
            setItems((prev) =>
              prev.map((item) =>
                item.id === id
                  ? { ...item, parsing: false, executedAt, wells: rematchWells(names) }
                  : item,
              ),
            );
          } catch (err) {
            if (cancelled) return;
            setItems((prev) =>
              prev.map((item) =>
                item.id === id
                  ? {
                      ...item,
                      parsing: false,
                      parseError: err instanceof Error ? err.message : "Error al leer TAM",
                    }
                  : item,
              ),
            );
          }
        }),
      );
    };

    void parseAll();
    return () => {
      cancelled = true;
    };
  }, [tamFiles]); // eslint-disable-line react-hooks/exhaustive-deps -- parse once per file set

  useEffect(() => {
    if (!locations.length && !activityWellHints.length) return;
    setItems((prev) =>
      prev.map((item) =>
        item.wells.length
          ? { ...item, wells: rematchWells(item.wells.map((w) => w.tamName)) }
          : item,
      ),
    );
  }, [locations, activityWellHints, rematchWells]);

  // Generalidades → TAM (también aplica a ítems que aún estaban parseando).
  useEffect(() => {
    if (syncingFromTamRef.current) {
      syncingFromTamRef.current = false;
      return;
    }
    if (!executedDate) return;
    const iso = defaultStudyDate(executedDate);
    setItems((prev) =>
      prev.map((item) => (item.parseError ? item : { ...item, executedAt: iso })),
    );
  }, [executedDate]);

  const applyStudyDate = (iso: string) => {
    setItems((prev) =>
      prev.map((item) => (item.parsing || item.parseError ? item : { ...item, executedAt: iso })),
    );
    const local = isoToLocalDate(iso);
    if (local && onExecutedDateChange) {
      syncingFromTamRef.current = true;
      onExecutedDateChange(local);
    }
  };

  const readyPayloads = useMemo((): TamConsolidateReadyPayload[] => {
    return items
      .filter((i) => !i.parsing && !i.parseError && i.wells.some((w) => w.location))
      .map((item) => ({
        file: item.file,
        executedAt: item.executedAt,
        locationIds: Array.from(new Set(item.wells.filter((w) => w.location).map((w) => w.location!.id))),
        wellNames: item.wells.map((w) => w.tamName),
      }));
  }, [items]);

  useEffect(() => {
    onReadyChange(readyPayloads);
  }, [readyPayloads, onReadyChange]);

  const updateWellLocation = (itemId: string, tamName: string, location: SimpleLocation | null) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        return {
          ...item,
          wells: item.wells.map((row) => {
            if (row.tamName !== tamName) return row;
            if (!location) return { ...row, location: null, matchKind: "none" as const, fromActivity: false };
            const rematch = matchTamWellToLocation(row.tamName, [location]);
            return {
              ...row,
              location,
              matchKind: rematch.location?.id === location.id ? rematch.kind : ("none" as const),
              fromActivity: false,
            };
          }),
        };
      }),
    );
  };

  if (!tamFiles.length) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">No hay archivos TAM en esta consolidación.</Typography>
      </Box>
    );
  }

  if (!selectedFieldId) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="warning.main">
          Seleccione el Campo en la pestaña Generalidades para asociar pozos TAM.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2, overflow: "auto", minHeight: 0, flex: 1 }}>
      {loadingLocs && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="caption">Cargando pozos del campo…</Typography>
        </Box>
      )}

      {activityWellHints.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {activityWellHints.length} pozo(s) validado(s) en actividades — se usan para pre-asignar coincidencias TAM.
        </Typography>
      )}

      {items.map((item) => (
        <Box key={item.id} className="tam-upload-item">
          <Box className="tam-upload-item__head">
            <span className="tam-upload-item__file-icon" aria-hidden>
              <InsertDriveFileOutlinedIcon fontSize="small" />
            </span>
            <span className="tam-upload-item__file-name" title={item.file.name}>
              {item.file.name}
            </span>
          </Box>

          {item.parsing && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Leyendo pozos del TAM…</Typography>
            </Box>
          )}

          {item.parseError && (
            <Typography variant="body2" color="error">
              {item.parseError}
            </Typography>
          )}

          {!item.parsing && !item.parseError && item.wells.length > 0 && (
            <>
              <TextField
                label="Fecha del estudio"
                type="date"
                size="small"
                value={item.executedAt}
                onChange={(e) => applyStudyDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ mb: 1.5 }}
                helperText="Conectada con Generalidades (Fecha ejecutadas actividad)."
              />

              <Box className="tam-upload-map-head">
                <span>Pozo en TAM</span>
                <span>Coincidencia BD</span>
                <span>Asignar pozo</span>
              </Box>

              {item.wells.map((row) => (
                <Box key={row.tamName} className="tam-upload-map-row">
                  <span className="tam-upload-map-row__tam" title={row.tamName}>
                    {row.tamName}
                  </span>
                  <Box className="tam-upload-map-row__match">
                    <MatchIcon kind={row.location ? row.matchKind : "none"} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" noWrap display="block">
                        {row.location ? row.location.name : "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {row.location
                          ? row.fromActivity
                            ? `${matchLabel(row.matchKind)} · actividad`
                            : matchLabel(row.matchKind)
                          : "Sin asignar"}
                      </Typography>
                    </Box>
                  </Box>
                  <Autocomplete
                    size="small"
                    options={locations}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={row.location}
                    onChange={(_, v) => updateWellLocation(item.id, row.tamName, v)}
                    openOnFocus={false}
                    filterOptions={tamLocationFilterOptions}
                    noOptionsText="Escribe para buscar pozo"
                    renderInput={(params) => <TextField {...params} placeholder="Buscar pozo…" />}
                    sx={{ minWidth: 0 }}
                  />
                </Box>
              ))}

              <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {item.wells.filter((w) => w.location).length === item.wells.length ? (
                  <Chip size="small" color="success" variant="outlined" label="Todos los pozos asignados" />
                ) : item.wells.some((w) => w.location) ? (
                  <>
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`${item.wells.filter((w) => w.location).length} se vincularán`}
                    />
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={`${item.wells.filter((w) => !w.location).length} sin asignar (no bloquean la carga)`}
                    />
                  </>
                ) : (
                  <Chip size="small" color="warning" variant="outlined" label="Asigna al menos un pozo para subir este TAM" />
                )}
              </Box>
            </>
          )}
        </Box>
      ))}
    </Box>
  );
}
